import { ICarouselHeroSlideFields } from '@/types/contentful';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import omit from 'lodash/omit';
import Image, { ImageLoaderProps } from 'next/legacy/image';
import { getImageData } from 'src/utils/contentful';
import 'swiper/css/bundle';
import CtaWidget from '../CtaWidget';
import GlImage, { GlImageProps } from '../Image';

type CarouselHeroSlideContentProps = ICarouselHeroSlideFields &
  Partial<GlImageProps> & {
    eagerLoadFirstNImages?: number;
    index: number;
  };

const imageLoader = ({ src }: ImageLoaderProps) => `https:${src}?fm=webp`;

const CarouselHeroSlideContent = ({
  desktopImage,
  headline,
  mobileImage,
  cta,
  disclosures,
  mobileBackgroundImage,
  eagerLoadFirstNImages,
  index,
  desktopGradientSpec,
  ...props
}: CarouselHeroSlideContentProps) => {
  const copy = (
    <>
      <h1 className="first-letter col-start-1 col-end-6 mb-1 text-center font-glRepro text-[30px] font-bold leading-[105%] tracking-[0.01em] text-white desktop:mb-0 desktop:h-auto desktop:text-start desktop:text-[48px] desktop:tracking-[-0.02em]">
        {headline}
      </h1>
      {cta && (
        <div className="col-start-1 col-end-6 ml-auto mr-auto mt-6 desktop:col-end-5 desktop:ml-0 desktop:mr-0 desktop:mt-9">
          <CtaWidget {...{ ...cta.fields }} />
        </div>
      )}
      {disclosures && (
        <div className="col-start-1 col-end-6 mt-5 flex-1 text-center desktop:col-end-4 desktop:mt-4 desktop:text-left [&>p>a]:underline [&>p]:font-glRepro [&>p]:text-xs [&>p]:leading-4 [&>p]:text-white">
          {documentToReactComponents(disclosures)}
        </div>
      )}
    </>
  );

  const desktopContent = (
    <div
      style={{ gridAutoRows: 'min-content' }}
      className="absolute left-0 top-0 z-10 grid h-full w-full grid-cols-12 gap-x-6 px-20 pt-36"
    >
      {copy}
    </div>
  );

  const mobileContent = (
    <div className="relative z-20 -mt-10 h-[250px]">
      <div className="absolute bottom-0 left-0 right-0 top-0 -z-10">
        <GlImage
          {...getImageData(mobileBackgroundImage)}
          heroImage={true}
          objectFit="cover"
          layout="fill"
          className="rounded-tl-3xl rounded-tr-3xl"
          /** Override explicitly set zIndex in GLImage */
          style={{ zIndex: 0 }}
        />
      </div>
      <div className="grid w-full grid-cols-5 gap-x-6 px-5 pb-7 pt-7 ">
        {copy}
      </div>
    </div>
  );

  const gradientDiv = desktopGradientSpec ? (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: desktopGradientSpec,
        zIndex: 1
      }}
    />
  ) : null;

  const mobileImageProps = omit(
    getImageData(mobileImage ?? desktopImage),
    'sysId',
    'contentType'
  );
  const desktopImageProps = omit(
    getImageData(desktopImage),
    'sysId',
    'contentType'
  );
  const isEager = index + 1 <= (eagerLoadFirstNImages ?? 1) ? true : false;

  return (
    <>
      <div className="max-h-full min-h-full w-full">
        <div className="hidden desktop:block">
          <Image
            {...props}
            {...desktopImageProps}
            loader={imageLoader}
            // Desktop needs to fill since we are using a fixed height.
            layout={'fill'}
            objectFit="cover"
            className="swiper-lazy gl-carousel-hero-slide-image h-[inherit]"
            priority={isEager}
            data-testid="gl-hero-img"
          />
        </div>
        <div className="block desktop:hidden">
          <Image
            {...props}
            {...mobileImageProps}
            loader={imageLoader}
            // Desktop needs to fill since we are using a fixed height.
            layout={'responsive'}
            objectFit="cover"
            className="swiper-lazy gl-carousel-hero-slide-image h-[inherit]"
            priority={isEager}
            data-testid="gl-hero-img"
          />
        </div>
      </div>
      {gradientDiv}
      <div className="hidden desktop:block">{desktopContent}</div>
      <div className="block desktop:hidden">{mobileContent}</div>
      {isEager ? null : (
        <div className="swiper-lazy-preloader swiper-lazy-preloader-white" />
      )}
    </>
  );
};

export default CarouselHeroSlideContent;
